//mdmAPI.ts

import { LAZY_CONFIG, apiGet, api } from "./API";


// ─── Types ────────────────────────────────────────────────────────────────────
export interface LineageSourceMeta {
  COLUMN_DISPLAY_NAME: string;
  ORIG_TABLE_NAME:     string;
  ORIG_COLUMN_NAME:    string;
  DATA_TYP:            string;
  COLUMN_SHORT_DESC?:  string;
  dataSrcSystemId?:    string;
  entityId?:           string;
}

export interface LineageEntry {
  entityId:        string;
  dataSrcSystemId: string;
  FLD_ID:          string;
  confidence:      number;
  match_type:      string;
  _sme_conf?:      number | null;
  _sme_match?:     string | null;
  resolved?:       LineageSourceMeta | null;   // server-resolved (lazy-safe)
}

export interface LineageField {
  FLD_ID:              string;
  COLUMN_NAME:         string;
  COLUMN_DISPLAY_NAME: string;
  COLUMN_SHORT_DESC?:  string;
  COLUMN_LONG_DESC?:   string;
  DATA_TYP:            string;
  MANDATORY?:          string;
  SEQ?:                number;
  source_lineage:      LineageEntry[];
}

export interface LineageSubEntity {
  name:        string;
  label:       string;
  description?: string;
  is_root:     boolean;
  total:       number;
  loaded:      number;
  has_more:    boolean;
  fields:      LineageField[];
}

export interface LineageStats {
  total_fields:        number;
  cross_system_fields: number;
  unmapped_fields:     number;
  avg_confidence:      number;   // 0..1
}

export interface LineageProfile {
  success:        boolean;
  entity_id:      string;
  entity_label:   string;
  source_systems: string[];
  stats:          LineageStats;
  sub_entities:   LineageSubEntity[];
}

export interface LineagePage {
  success:    boolean;
  sub_entity: string;
  offset:     number;
  limit:      number;
  total:      number;
  has_more:   boolean;
  fields:     LineageField[];
}

export interface MatchField {
  field:      string;   // e.g. "GEMINAI-ErpVendorIdentifier"
  origfield:  string;   // e.g. "ErpVendorIdentifier"
  match_type: string;   // "EXACT" | "FUZZY" | ...
  weight:     number;   // 0..100
}

export interface MatchRule {
  rule_name:    string;
  description:  string;
  match_fields: MatchField[];
  confidence:   number;                 // 0..100
  merge_action: "auto-merge" | "manual-review" | string;
}

export interface MatchRulesResponse {
  success:              boolean;
  entity_id:            string;
  entity_label:         string;
  total:                number;
  match_rules:          MatchRule[];
  by_action:            Record<string, number>;
  auto_merge_count:     number;
  manual_review_count:  number;
}

// ─── Endpoints (BASE assumed = "/adriva_api/dm"; align with getDMProfile) ──────
const MDM_BASE = "/adriva_api/mdm";


/** First page of every sub-entity for one canonical entity + entity-wide stats. */
export async function getLineageProfile(
  appNm: string,
  entityId: string,
  pageSize: number = LAZY_CONFIG.PAGE_SIZE,
): Promise<LineageProfile> {
  return apiGet(
    `${MDM_BASE}/lazy/lineage/${encodeURIComponent(appNm)}/${encodeURIComponent(entityId)}`,
    { page_size: pageSize },
  );
}

/** Next page of fields for ONE sub-entity. */
export async function getLineagePage(
  appNm: string,
  entityId: string,
  subEntity: string,
  offset: number,
  limit: number = LAZY_CONFIG.PAGE_SIZE,
): Promise<LineagePage> {
  return apiGet(
    `${MDM_BASE}/lazy/lineage-page/${encodeURIComponent(appNm)}`
    + `/${encodeURIComponent(entityId)}/${encodeURIComponent(subEntity)}`,
    { offset, limit },
  );
}

/** Autocomplete search over source FLD_IDs (for the Add-mapping form). */
export async function searchLineageSourceFields(
  appNm: string,
  q: string,
  limit: number = 12,
): Promise<{ success: boolean; results: Array<LineageSourceMeta & { FLD_ID: string }> }> {
  return apiGet(
    `${MDM_BASE}/lazy/lineage-src-search/${encodeURIComponent(appNm)}`,
    { q, limit },
  );
}

/**
 * Match (dedup) rules for one entity's canonical model.
 * Endpoint: GET /adriva_api/mdm/lazy/match-rules/{appNm}/{entityId}
 */
export async function getMatchRules(
  appNm: string,
  entityId: string,
): Promise<MatchRulesResponse> {
  return apiGet(
    `${MDM_BASE}/lazy/match-rules/${encodeURIComponent(appNm)}/${encodeURIComponent(entityId)}`,
  );
}


// ════════════════════════════════════════════════════════════════════════════
// Crosswalk
// ════════════════════════════════════════════════════════════════════════════

// ─── Crosswalk types ──────────────────────────────────────────────────────────

export type SMEStatus = 'APPROVED' | 'REJECTED' | 'NEEDS_REVIEW' | null;
export type RowType   = 'FULL_CHAIN' | 'PARTIAL_CHAIN' | 'S4_ONLY' | 'SOURCE_ONLY' | 'TARGET_ONLY';
export type ConfBucket= 'CONFIRMED' | 'HIGH' | 'PROBABLE' | 'POSSIBLE' | 'NO_LINEAGE';

export interface CrosswalkSourceCell {
  fld_id:            string;
  display_name:      string;
  orig_col:          string;
  table:             string;
  data_type:         string;
  data_len?:         string;
  short_desc?:       string;
  long_desc?:        string;
  sap_dtel?:         string;
  entity_type?:      string;
  segment?:          string;
  element?:          string;
  match_type:        string;
  confidence:        number;
  confidence_bucket: ConfBucket;
  match_reason:      string;
  analyst_note:      string;
}

export interface CrosswalkRow {
  // Target (MDG) side
  target_fld_id:       string | null;
  target_display_name: string | null;
  target_short_desc:   string | null;
  target_long_desc:    string | null;
  target_table:        string | null;
  target_data_type:    string | null;
  target_data_len:     string | null;
  target_orig_col:     string | null;
  target_mandatory:    string | null;
  target_sap_dtel:     string | null;
  target_entity_type:  string | null;
  target_sap_segment:  string | null;
  target_sap_element:  string | null;

  // Row classification
  _row_type:           RowType;
  _category?:          string;      // for S4_ONLY rows
  _analyst_note?:      string;
  _probable_confidence?: number;
  _confidence_bucket?: ConfBucket;

  // SME annotations (written by the UI, persisted by the server)
  _sme_status?:        SMEStatus;
  _sme_comment?:       string;
  _sme_conf_override?: number | null;
  _sme_verified_at?:   string;
  _sme_verified_by?:   string;

  // Source system cells — keyed by dataSrcSystemId (e.g. "S4HANA", "MBC")
  [srcSystemId: string]: CrosswalkSourceCell | any;
}

export interface CrosswalkSMESummary {
  total:       number;
  approved:    number;
  rejected:    number;
  needs_review:number;
  pending:     number;
  pct_reviewed:number;
}

export interface CrosswalkProfile {
  success:         boolean;
  entity_id:       string;
  entity_label:    string;
  metadata:        Record<string, any>;
  sme_summary:     CrosswalkSMESummary;
  source_systems:  string[];
  total_filtered:  number;
  total_rows:      number;
  page:            number;
  page_size:       number;
  total_pages:     number;
  rows:            CrosswalkRow[];
  available_tables:string[];
  row_type_counts: Record<string, number>;
  preset_counts:   Record<string, number>;   // count per perspective:key preset
}

export interface SMERemapPayload {
  row_index:          number;
  source_sys_id:      string;
  new_target_fld_id?: string | null;    // null = remove mapping
  sme_comment?:       string;
  sme_verified_by?:   string;
}

export interface SMEAddMappingPayload {
  source_sys_id:    string;
  source_fld_id:    string;
  target_fld_id?:   string;
  sme_comment?:     string;
  sme_verified_by?: string;
}

export interface SMERowPatch {
  row_index:         number;
  sme_status?:       SMEStatus;
  sme_comment?:      string;
  sme_conf_override?: number | null;
  sme_verified_by?:  string;
}

export interface CrosswalkPatchResponse {
  success:     boolean;
  row_index:   number;
  row:         CrosswalkRow;
  sme_summary: CrosswalkSMESummary;
}

export interface CrosswalkBatchResponse {
  success:     boolean;
  updated:     number;
  errors:      string[];
  sme_summary: CrosswalkSMESummary;
}

export interface CrosswalkFilters {
  page?:        number;
  page_size?:   number;
  row_type?:    RowType | '';
  conf_bucket?: ConfBucket | '';
  table?:       string;
  q?:           string;
  sme_status?:  string;
  source_sys?:  string;
  perspective?: string;   // e.g. "MBC:mapped", "S4HANA:probable_ext", "MDG:full_chain"
}

// ─── API functions ────────────────────────────────────────────────────────────

/**
 * Fetch a filtered, paginated page of crosswalk rows.
 * All filtering is server-side — never download the full 1 000+ row file.
 */
export async function getCrosswalkProfile(
  appNm:    string,
  entityNm: string,   // business entity name e.g. "Supplier", NOT a UUID
  filters:  CrosswalkFilters = {},
): Promise<CrosswalkProfile> {
  // Build params object — apiGet handles the URL query encoding.
  // Only include non-empty values so we don't send `&row_type=` etc.
  const params: Record<string, any> = {};
  if (filters.page        != null) params.page        = filters.page;
  if (filters.page_size   != null) params.page_size   = filters.page_size;
  if (filters.row_type)            params.row_type    = filters.row_type;
  if (filters.conf_bucket)         params.conf_bucket = filters.conf_bucket;
  if (filters.table)               params.table       = filters.table;
  if (filters.q)                   params.q           = filters.q;
  if (filters.sme_status)          params.sme_status  = filters.sme_status;
  if (filters.source_sys)          params.source_sys  = filters.source_sys;
  if (filters.perspective)         params.perspective = filters.perspective;

  return apiGet(
    `${MDM_BASE}/lazy/lineage-crosswalk/${encodeURIComponent(appNm)}/${encodeURIComponent(entityNm)}`,
    params,
  );
}

/** Patch a single crosswalk row with SME annotations. */
export async function patchCrosswalkRow(
  appNm:    string,
  entityNm: string,   // business entity name e.g. "Supplier"
  patch:    SMERowPatch,
): Promise<CrosswalkPatchResponse> {
  const res = await api.patch<CrosswalkPatchResponse>(
    `${MDM_BASE}/lazy/lineage-crosswalk/${encodeURIComponent(appNm)}/${encodeURIComponent(entityNm)}/row`,
    patch,
  );
  return res.data;
}

/** Batch-patch multiple crosswalk rows (approve/reject many at once). */
export async function batchPatchCrosswalkRows(
  appNm:    string,
  entityNm: string,   // business entity name e.g. "Supplier"
  patches:  SMERowPatch[],
): Promise<CrosswalkBatchResponse> {
  const res = await api.post<CrosswalkBatchResponse>(
    `${MDM_BASE}/lazy/lineage-crosswalk/${encodeURIComponent(appNm)}/${encodeURIComponent(entityNm)}/batch`,
    { patches },
  );
  return res.data;
}

/** Remap or remove a source cell on a crosswalk row (SME correction). */
export async function remapCrosswalkRow(
  appNm:    string,
  entityNm: string,
  payload:  SMERemapPayload,
): Promise<CrosswalkPatchResponse> {
  const res = await api.patch<CrosswalkPatchResponse>(
    `${MDM_BASE}/lazy/lineage-crosswalk/${encodeURIComponent(appNm)}/${encodeURIComponent(entityNm)}/remap`,
    payload,
  );
  return res.data;
}

/** Add a new source→target mapping the agent missed (SME manual add). */
export async function addCrosswalkMapping(
  appNm:    string,
  entityNm: string,
  payload:  SMEAddMappingPayload,
): Promise<CrosswalkPatchResponse> {
  const res = await api.post<CrosswalkPatchResponse>(
    `${MDM_BASE}/lazy/lineage-crosswalk/${encodeURIComponent(appNm)}/${encodeURIComponent(entityNm)}/add-mapping`,
    payload,
  );
  return res.data;
}

/**
 * Download the crosswalk as a server-generated Excel workbook (.xlsx).
 * The server streams application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.
 * Triggers a browser file-save dialog — no client-side processing needed.
 */
export async function exportCrosswalkExcel(
  appNm:    string,
  entityNm: string,   // business entity name e.g. "Supplier"
): Promise<void> {
  // GET the .xlsx as a binary blob. responseType:'blob' is critical — without
  // it axios tries to JSON.parse the binary and corrupts it.
  const res = await api.get<Blob>(
    `${MDM_BASE}/lazy/lineage-crosswalk/${encodeURIComponent(appNm)}/${encodeURIComponent(entityNm)}/export`,
    {
      responseType: 'blob',
      timeout:      10 * 60 * 1000,   // large workbooks can take a while
    },
  );

  // Trigger browser "Save As" dialog
  const blob = res.data;
  const href = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = href;
  a.download = `${appNm}_${entityNm}_lineage_crosswalk.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(href);
}